import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { RfpApiPotentialSeller } from './rfp-potential-seller';
import { RfpParticipant } from './rfp-participant';
import { FileLikeObject } from '@aptitude/angular-pipes';
import { mapAccepted } from '../../util/mapAccepted';
import { handleError } from '../../util/handleError';
import { handleErrorSilently } from '../../util/handleErrorSilently';
import { Attachment } from '../../types/attachment';
import { DownloadUtilService } from '../../../services/download-util.service';
import { RestRfp } from '../../../types/rfp/rest-rfp';
import { GenericName } from '../../../types/rfp/genericname';
import {
  CommitmentMixModel,
  MixQueryParam,
} from '../../../types/purchase-plan/commitment-mix-model';
import { noDefaultErrorHandler } from '../../../services/interceptors/error-handler/operators/no-default-error-handler.operator';
import { SpendByMonth } from '../../../types/rfp-spend/spend-by-month';
import { RfpSpend } from '../../../types/rfp-spend/rfp-spend';
import { PurchasePlan } from '../../../types/purchase-plan';
import { Offer } from '../../../types/rfp';
import { RfpOfferSavings } from '../../../types/rfp-offer-savings';
import {
  SpendByUnspsc,
  SpendByBuyer,
  SpendByParticipants,
} from '../../../types/rfp-spend';
import {
  getCategorySchemeNameById,
  PublicCategorizationSchemes,
} from '@aptitude/shared-vanilla';
import { CategorizationSchemesService } from '@aptitude/shared';

@Injectable({
  providedIn: 'root',
})
export class RfpApiService {
  getBaseUrl(rfpId: number): string {
    return `/aptitude/api/rfps/${rfpId}`;
  }

  getPurchasePlanUrl(purchasePlanId: number): string {
    return `/aptitude/api/purchaseplans/${purchasePlanId}`;
  }

  getBuyerImpactUrl(): string {
    return `/aptitude/buyer/GroupImpactServiceController`;
  }

  constructor(
    private http: HttpClient,
    private downloadUtilService: DownloadUtilService,
    private categorizationSchemesService: CategorizationSchemesService,
  ) {}

  /**
   * Retrieves an RFP based on the provided RFP ID.
   * When `useNewPscLabel` is set to true, it will fetch the contract categorization schemes
   * and replace the PSC label with "CC" if applicable.
   * @param rfpId - The unique identifier of the RFP to retrieve.
   * @param useNewPscLabel - A boolean flag indicating whether to use the new PSC label
   *                         for categorization. Defaults to `false`.
   * @returns An observable that emits the retrieved `RestRfp` object.
   */
  getRfp(rfpId: number, useNewPscLabel = false): Observable<RestRfp> {
    if (useNewPscLabel) {
      return this.categorizationSchemesService.categorizationSchemes$.pipe(
        switchMap((categorizationSchemes) => {
          return this.http.get(`${this.getBaseUrl(rfpId)}?version=2.0`).pipe(
            map((response) => {
              if (
                (response as any).categorizationSchemeId ===
                PublicCategorizationSchemes.CC
              ) {
                (response as any).categorizationSchemeName =
                  getCategorySchemeNameById(
                    (response as any).categorizationSchemeId,
                    categorizationSchemes,
                  );
              }
              return new RestRfp(response);
            }),
          );
        }),
      );
    } else {
      return this.http
        .get(`${this.getBaseUrl(rfpId)}?version=2.0`)
        .pipe(map((response) => new RestRfp(response)));
    }
  }

  /** @description Submit rfp to Seller */
  submitRfpSendStatus(rfpId: number) {
    return this.http
      .put(`${this.getBaseUrl(rfpId)}/status`, {
        status: 'SEND',
      })
      .pipe(map((response) => new RestRfp(response)));
  }

  /*@deprecated Will soon be replaced with GraphQL version*/
  getPotentialCategories(rfpId: number) {
    return this.http.get(`${this.getBaseUrl(rfpId)}/potentialcategories-se`);
  }

  /*@deprecated Will soon be replaced with GraphQL version*/
  getPotentialSellers(rfpId: number): Observable<Array<RfpApiPotentialSeller>> {
    return this.http.get(
      `${this.getBaseUrl(rfpId)}/potentialsellers`,
    ) as Observable<Array<RfpApiPotentialSeller>>;
  }

  /** @deprecated use new GraphQL endpoint instead */
  getRfpMixes(rfpId: number) {
    return this.http.get(`${this.getBaseUrl(rfpId)}/mixes`);
  }

  getRfpPurchasePlans(rfpId: number) {
    return this.http.get(`${this.getBaseUrl(rfpId)}/purchaseplans`);
  }

  getGenericPlanName(rfpId: number): Observable<string> {
    return this.http
      .get<GenericName>(`${this.getBaseUrl(rfpId)}/genericplanname`)
      .pipe(map((response) => response.genericName));
  }

  getRfpMixType(
    rfpId: number,
    type: string,
    useLockedAllocation: boolean,
    usePrimaryCrossReferences: boolean,
    offers = '',
    duration?: number,
    pricingType?: string,
    mixQueryParam?: MixQueryParam, //deprecated
    tierId?: number,
  ) {
    let params: HttpParams = new HttpParams();
    const offerCollection =
      offers !== '' ? offers : mixQueryParam?.offers || '';
    // HttpParams are immutable, we have to take the output of the set functions to get all the changes.
    params = params
      .set('offers', offerCollection)
      .set('useLockedAllocations', Boolean(useLockedAllocation).toString())
      .set(
        'usePrimaryCrossReferences',
        Boolean(usePrimaryCrossReferences).toString(),
      );

    if (mixQueryParam?.selectedTerm) {
      params = params
        .set('duration', mixQueryParam.selectedTerm?.duration)
        .set('pricingType', mixQueryParam.selectedTerm?.pricingType);

      if (mixQueryParam.selectedTerm.tierId) {
        //Deprecated
        params = params.set('tierId', mixQueryParam.selectedTerm?.tierId);
      }
    } else {
      params = params
        .set('duration', duration || '')
        .set('pricingType', pricingType || '');
      if (tierId) {
        params = params.set('tierId', tierId);
      }
    }

    return this.http
      .get(`${this.getBaseUrl(rfpId)}/mixes/${type}`, {
        observe: 'response',
        params,
      })
      .pipe(
        map(mapAccepted()),
        map((mixType) => mixType as CommitmentMixModel),
        noDefaultErrorHandler(),
        catchError(handleError),
      );
  }

  getRfpSpendByMonth(rfpId: number): Observable<Array<SpendByMonth>> {
    return this.http
      .get<RfpSpend>(`${this.getBaseUrl(rfpId)}/spend?fields=ByMonth`)
      .pipe(map((response) => response.spendByMonths));
  }

  getRfpSpendByUnspsc(rfpId: number): Observable<Array<SpendByUnspsc>> {
    return this.http
      .get<RfpSpend>(`${this.getBaseUrl(rfpId)}/spend?fields=ByUnspsc`)
      .pipe(map((response) => response.spendByUnspscs));
  }

  getRfpSpendByBuyer(rfpId: number): Observable<SpendByBuyer> {
    return this.http
      .get<RfpSpend>(`${this.getBaseUrl(rfpId)}/spend?fields=ByBuyer`)
      .pipe(
        map((response) => {
          // TODO: This is a temporary hack. The Spend dates are at the top of the endpoint.
          // To get this data with the current api design we should implement a new function here that calls the enpoint with no fields
          // And then brought into the page through a new resolver
          // This is only used by TopSellers Component
          response.spendByBuyer.rfpSpendDates = response.rfpSpendDates;
          return response.spendByBuyer;
        }),
      );
  }

  getRfpSpendByParticipant(
    rfpId: number,
  ): Observable<Array<SpendByParticipants>> {
    return this.http
      .get<RfpSpend>(`${this.getBaseUrl(rfpId)}/spend?fields=ByParticipant`)
      .pipe(
        map((response) =>
          response.spendByParticipants.map(
            (spendByParticipant) => new SpendByParticipants(spendByParticipant),
          ),
        ),
      );
  }

  getPurchasePlan(rfpId: number): Observable<PurchasePlan> {
    return this.http.get<PurchasePlan>(
      `${this.getBaseUrl(rfpId)}/purchaseplan`,
    );
  }

  getPurchasePlanMember(
    rfpId: number,
    memberId: number,
  ): Observable<PurchasePlan> {
    return this.http.get<PurchasePlan>(
      `${this.getBaseUrl(rfpId)}/purchaseplan?memberId=${memberId}`,
    );
  }

  getRfpAttachments(rfpId: number): Observable<Array<Attachment>> {
    return this.http.get<Array<Attachment>>(
      `${this.getBaseUrl(rfpId)}/documents`,
    );
  }

  /**
   * @deprecated: This method does not belong here. Please put in DownloadApiService file.
   */
  downLoadLineItemDetail(rfpId: number) {
    return this.downloadUtilService.download(
      `/aptitude/download/rfps/${rfpId}/lineitemdetails`,
      `lineitemdetails_${rfpId}.xlsx`,
    );
  }

  getRfpOfferSavings(rfpId: number) {
    return this.http
      .get<Array<RfpOfferSavings>>(`${this.getBaseUrl(rfpId)}/offers`, {
        observe: 'response',
      })
      .pipe(
        map(mapAccepted()),
        noDefaultErrorHandler(),
        catchError(handleError),
      ) as Observable<Array<RfpOfferSavings>>;
  }

  /**
   * @deprecated: This method does not belong here. Please put in PurchasePlansApiService file.
   */
  setSelectedOffers(
    planId: number,
    selectedOffers: [{ offerId: number; selected: boolean }],
  ) {
    // handleErrorSilently is the alternate version of handleError. It does not let anything bubble
    // up to the caller in the event of a 202, it is just handled and retried.
    return this.http
      .put(`/aptitude/api/purchaseplans/${planId}/selections`, selectedOffers, {
        observe: 'response',
      })
      .pipe(
        map(mapAccepted()),
        noDefaultErrorHandler(),
        catchError(handleErrorSilently),
      );
  }

  /**
   * This gets the member savings and handles automatic retries if the data is not yet available.
   *
   * @returns Expect EMPTY to arrive if the data doesn't come in immediately. You can put up a message
   * or just swallow those responses. The actual data will be returned as soon as the API call succeeds.
   */
  getRfpOfferMemberSavings(rfpId: number, offerId: string) {
    return this.http
      .get(`${this.getBaseUrl(rfpId)}/offers/${offerId}/membersavings`, {
        observe: 'response',
      })
      .pipe(
        map(mapAccepted()),
        noDefaultErrorHandler(),
        catchError(handleError),
      );
  }

  // We'll get a 204 No Content response if this succeeds
  loadBestMix(
    rfpId: number,
    usePrimaryCrossReferences: boolean,
    useLockedAllocations: boolean,
    purchasePlanId?: number,
  ) {
    return this.http.put(`${this.getBaseUrl(rfpId)}/purchaseplan`, {
      source: 'BEST_MIX',
      usePrimaryCrossReferences,
      useLockedAllocations,
      purchasePlanId,
    });
  }

  // We'll get a 204 No Content response if this succeeds
  loadCurrentMix(
    rfpId: number,
    usePrimaryCrossReferences: boolean,
    useLockedAllocations: boolean,
    purchasePlanId?: number,
  ) {
    return this.http.put(`${this.getBaseUrl(rfpId)}/purchaseplan`, {
      source: 'CURRENT_MIX',
      usePrimaryCrossReferences,
      useLockedAllocations,
      purchasePlanId,
    });
  }

  // We'll get a 204 No Content response if this succeeds
  loadOptimizedMix(
    rfpId: number,
    optimizedForOfferId: number,
    selectedTerm: any,
    usePrimaryCrossReferences: boolean,
    useLockedAllocations: boolean,
    purchasePlanId?: number,
  ) {
    return this.http.put(`${this.getBaseUrl(rfpId)}/purchaseplan`, {
      source: 'OPTIMIZED_FOR_OFFER_MIX',
      purchasePlanId,
      optimizedForOfferId,
      selectedTierId: selectedTerm.tierId,
      selectedTerm: {
        duration: selectedTerm.duration,
        pricingType: selectedTerm.pricingType,
      },
      usePrimaryCrossReferences,
      useLockedAllocations,
    });
  }

  loadAllocateAllMix(rfpId: number, purchasePlanId?: number) {
    return this.http.put(`${this.getBaseUrl(rfpId)}/purchaseplan`, {
      source: 'ALLOCATE_ALL_MIX',
      purchasePlanId,
    });
  }

  // We'll get a 204 No Content response if this succeeds
  loadSavedPlan(rfpId: number, purchasePlanId: number) {
    return this.http.put(`${this.getBaseUrl(rfpId)}/purchaseplan`, {
      source: 'SAVED_PLAN',
      purchasePlanId,
    });
  }

  savePlan(rfpId: number, newPlanName: string) {
    return this.http.post(`${this.getBaseUrl(rfpId)}/purchaseplans`, {
      purchasePlanName: newPlanName,
    });
  }

  getSystems(rfpId: number): Observable<Array<RfpParticipant>> {
    return this.http.get(`${this.getBaseUrl(rfpId)}/systems`) as Observable<
      Array<RfpParticipant>
    >;
  }

  updateParticipants(rfpId: number, participants: Array<unknown>) {
    return this.http.put(
      `${this.getBaseUrl(rfpId)}/participants`,
      participants,
    );
  }

  renameCategory(rfpId: number, categoryName: string) {
    return this.http.put(`${this.getBaseUrl(rfpId)}/category`, {
      categoryName,
    });
  }

  addSeller(rfpId: number, sellerId: number) {
    return this.http.post(`${this.getBaseUrl(rfpId)}/sellers`, {
      ids: [sellerId], // this takes in an array
    });
  }

  removeSeller(rfpId: number, sellerId: number) {
    return this.http.delete(`${this.getBaseUrl(rfpId)}/sellers/${sellerId}`);
  }

  uploadAttachment(file: FileLikeObject, rfpId: number): Observable<any> {
    const formData = new FormData();
    formData.append('files', file.rawFile, file.name);

    return this.http.post(
      `${this.getBaseUrl(rfpId)}/documents`,
      formData,
    ) as Observable<any>;
  }

  deleteAttachment(rfpId: number, fileId: string): Observable<any> {
    return this.http.delete(
      `${this.getBaseUrl(rfpId)}/documents/${fileId}`,
    ) as Observable<any>;
  }

  getSellerOffers(rfpId: number, organizationId: number): Observable<Offer[]> {
    return this.http.get<Offer[]>(
      `${this.getBaseUrl(rfpId)}/sellers/${organizationId}/offers`,
    );
  }

  submitResponse(
    rfpId: number,
    organizationId: number,
    offers: any,
  ): Observable<any> {
    return this.http.put(
      `${this.getBaseUrl(rfpId)}/sellers/${organizationId}/offers`,
      {
        offers: offers,
        responseStatus: 'OfferSubmitted',
      },
    );
  }

  retractResponse(rfpId: number, organizationId: number): Observable<any> {
    return this.http.put(
      `${this.getBaseUrl(rfpId)}/sellers/${organizationId}/offers`,
      {
        responseStatus: 'OfferRetracted',
      },
    );
  }

  // I know this is using a different base URL. however, it is only one method so far.
  // In case we need to use some others legacy endpoints for purchase plan I will move this
  // to a separate service.
  getMasterPlanId(purchasePlanId: number): Observable<any> {
    return this.http.get<any>(
      `${this.getPurchasePlanUrl(purchasePlanId)}/masterPlanId`,
    );
  }

  getMasterPlanInfo(masterPlanId: number): Observable<any> {
    return this.http.get<any>(
      `${this.getBuyerImpactUrl()}/getMasterPlanInfo?masterPlanId=${masterPlanId}`,
    );
  }
}
